import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getDoc, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ownerStamp } from '../recordOwner';
import '../theme-palette.css';
import { Box, CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { useT } from '../i18n.jsx';

const InventoryUser = () => {
  const { t } = useT();
  const statusWord = (s) => t(
    `inventoryUser.statuses.${String(s || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, s,
  );
  const [myInventory, setMyInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Application Form State
  const [showAppForm, setShowAppForm] = useState(null); // stores the inventory item being applied for
  const [appType, setAppType] = useState('Report Discrepancy');
  const [remarks, setRemarks] = useState('');

  const user = auth.currentUser;
  const { activeSchoolId } = useOutletContext();

  useEffect(() => {
    if (activeSchoolId) fetchData();
  }, [activeSchoolId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (user?.email && activeSchoolId) {
        // Resolve user's initial
          let accountId = user.email.toLowerCase();
          const indexDoc = await getDoc(doc(db, 'schools', activeSchoolId, 'email_index', accountId));
          if (indexDoc.exists() && indexDoc.data().accountId) {
            accountId = indexDoc.data().accountId;
          }
  
          // Fetch inventory assigned to me
          const iSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'inventory'), where('assignedTo', '==', accountId)));
        const iList = [];
        iSnap.forEach(d => iList.push({ uid: d.id, ...d.data() }));
        setMyInventory(iList);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleStocktakeNormal = async (item) => {
    if (!window.confirm(t('inventoryUser.stocktakeConfirm', { quantity: item.quantity, name: item.name }))) return;
    try {
      await updateDoc(doc(db, 'schools', activeSchoolId, 'inventory', item.uid), {
        lastStocktake: new Date().toISOString()
      });
      alert(t('inventoryUser.stocktakeDone'));
      fetchData();
    } catch (e) {
      alert(t('inventoryUser.error', { message: e.message }));
    }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!showAppForm || !remarks) return;
    try {
      let accountId = user.email.toLowerCase();
      const indexDoc = await getDoc(doc(db, 'schools', activeSchoolId, 'email_index', accountId));
      if (indexDoc.exists() && indexDoc.data().accountId) {
        accountId = indexDoc.data().accountId;
      }

      await addDoc(collection(db, 'schools', activeSchoolId, 'inventory_applications'), { ...ownerStamp(),
        inventoryId: showAppForm.uid,
        schoolId: activeSchoolId,
        itemName: showAppForm.name,
        type: appType,
        remarks: remarks,
        submittedBy: accountId,
        // Verifiable identity for the rule; submittedBy stays for display and existing queries.
        submittedByEmail: (user.email || '').toLowerCase(),
        requestDate: serverTimestamp(),
        status: 'Pending'
      });
      
      alert(t('inventoryUser.submitted'));
      setShowAppForm(null);
      setRemarks('');
      setAppType('Report Discrepancy');
    } catch (e) {
      alert(t('inventoryUser.error', { message: e.message }));
    }
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      
      {/* Top Tabs */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {[
            { id: 'inventory', label: t('inventoryUser.tab'), icon: Box }
          ].map(tab => (
            <button key={tab.id} className="theme-tab active" style={{ whiteSpace: 'nowrap' }}>
              <div className="theme-tab-icon"><Box size={18} /></div>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Application Modal Overlay */}
      {showAppForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '500px', boxShadow: 'var(--shadow-lg)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: 'var(--color-primary)' }}>{t('inventoryUser.submitApplication')}</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem' }}>{t('inventoryUser.itemLabel')} <strong>{showAppForm.name}</strong> ({showAppForm.id})</p>
            
            <form onSubmit={submitApplication} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryUser.applicationType')}</label>
                <select className="input-field" value={appType} onChange={e => setAppType(e.target.value)} required>
                  <option value="Report Discrepancy">{t('inventoryUser.typeDiscrepancy')}</option>
                  <option value="Write-off">{t('inventoryUser.typeWriteOff')}</option>
                  <option value="Replacement">{t('inventoryUser.typeReplacement')}</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryUser.remarks')}</label>
                <textarea className="input-field" rows="4" placeholder={t('inventoryUser.remarksPlaceholder')} value={remarks} onChange={e => setRemarks(e.target.value)} required></textarea>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowAppForm(null)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>{t('inventoryUser.submit')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div className="theme-card-container">
        <div className="theme-card-header">
          {t('inventoryUser.assigned')}
        </div>

        <div className="theme-card-content">
          {loading ? (
            <div className="text-muted text-center py-4">{t('inventoryUser.loading')}</div>
          ) : myInventory.length === 0 ? (
            <div className="text-muted text-center py-4">{t('inventoryUser.none')}</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: '1.5rem' }}>
              {myInventory.map(item => {
                const stocktakeDue = !item.lastStocktake || (new Date() - new Date(item.lastStocktake)) > 1000 * 60 * 60 * 24 * 30 * 6; // 6 months example
                
                return (
                  <div key={item.uid} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', display: 'flex', flexDirection: 'column', backgroundColor: item.status !== 'Active' ? 'var(--color-bg-subtle)' : 'white' }}>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', fontSize: '1.1rem', color: 'var(--color-text-main)' }}>
                          <Box size={18} color="var(--color-primary)" />
                          {item.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{item.id}</div>
                      </div>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: item.status === 'Active' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
                        color: item.status === 'Active' ? 'var(--color-success-text)' : 'var(--color-danger-text)'
                      }}>
                        {statusWord(item.status)}
                      </span>
                    </div>
                    
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem', flex: 1 }}>
                      <div><strong>{t('inventoryUser.category')}</strong> {item.category}</div>
                      <div><strong>{t('inventoryUser.quantityAssigned')}</strong> {item.quantity}</div>
                      <div style={{ marginTop: '0.5rem', color: stocktakeDue ? 'var(--color-warning-text)' : 'var(--color-text-muted)' }}>
                        <strong>{t('inventoryUser.lastStocktake')}</strong> {item.lastStocktake ? new Date(item.lastStocktake).toLocaleDateString() : t('inventoryUser.never')}
                      </div>
                    </div>

                    {item.status === 'Active' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <button onClick={() => handleStocktakeNormal(item)} className="btn-primary" style={{ backgroundColor: 'var(--color-success)', color: 'var(--color-text-main)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}>
                          <CheckCircle size={16} /> {t('inventoryUser.confirmPossession')}
                        </button>
                        <button onClick={() => setShowAppForm(item)} className="btn-outline" style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}>
                          <AlertTriangle size={16} /> {t('inventoryUser.reportDiscrepancy')}
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', textAlign: 'center', fontStyle: 'italic' }}>
                        {t('inventoryUser.inactive', { status: statusWord(item.status) })}
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default InventoryUser;
